/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ProjectParams } from 'types/project';
import {
  getCommentCountsByVersions,
  getCommentCountsFulfilled,
  getRibbonPanelItem,
} from 'features/comments/commentsSlice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCommentCountsByContentId } from 'features/comments/commentService';
import { CommentEventType, useEventSubscription } from '@pwc/event-hub-ui';
import {
  CommentCounts,
  CommentThreadsState,
  CommentThreatsSubscription,
  CommentViewStatus,
  GetCommentCountsByVersionsReturnType,
  UseCommentCountsParams,
} from './useCommentCounts.types';

// Content Id from useAnalysesContentId wasn't upto date.
import { viewingStatusCommentingPanel } from '../../features/hypotheses/hypothesisSlice';
import { useAnalysesContentId } from './useAnalysesContentId';

const selectCommentCountsByVersions = getCommentCountsByContentId as any;

export const useCommentCounts = ({
  isShared,
  viewingStatus,
  isPanelOpened,
}: UseCommentCountsParams) => {
  const { projectId } = useParams<ProjectParams>();
  const dispatch = useDispatch();
  const contentId = useAnalysesContentId();
  const viewingStatusFromCommentsMFE = useSelector(
    viewingStatusCommentingPanel
  );
  const commentCountsByVersion = useSelector(
    getCommentCountsByVersions
  ) as GetCommentCountsByVersionsReturnType;

  const commentCountsFulfilled = useSelector(
    getCommentCountsFulfilled
  ) as boolean;
  const ribbonPanelItemType = useSelector(getRibbonPanelItem);

  const [commentThreads, setCommentThreads] =
    useState<CommentThreadsState | null>(null);

  const [apiCommentCounts, setApiCommentCounts] = useState<CommentCounts>({
    contentId: 0,
    resolved: 0,
    unresolved: 0,
  });

  const [listCommentCounts, setListCommentCounts] = useState<CommentCounts>({
    contentId: 0,
    resolved: 0,
    unresolved: 0,
  });

  useEffect(() => {
    if (projectId && contentId) {
      dispatch(
        selectCommentCountsByVersions({
          projectId,
          contentId,
        })
      );
    }
  }, [contentId, dispatch, projectId, isPanelOpened]);

  const wasPanelOpened = !!commentThreads;

  const isCommentThreadsListAvailable =
    isPanelOpened && commentThreads?.threadsState?.isSuccess;

  const isCommentThreadApiAvailable =
    commentCountsByVersion && commentCountsFulfilled;

  useEffect(() => {
    if (isCommentThreadApiAvailable) {
      let commentCountsValue: CommentCounts & { contentId: number };
      if (isShared) {
        commentCountsValue = {
          contentId: commentCountsByVersion.contentId,
          resolved: commentCountsByVersion.resolvedSharedThreadCount,
          unresolved: commentCountsByVersion.unresolvedSharedThreadCount,
        };
      } else {
        commentCountsValue = {
          contentId: commentCountsByVersion.contentId,
          resolved: commentCountsByVersion.resolvedDraftThreadCount,
          unresolved: commentCountsByVersion.unresolvedDraftThreadCount,
        };
      }

      setApiCommentCounts(commentCountsValue);
    }
  }, [
    apiCommentCounts.contentId,
    commentCountsByVersion,
    commentCountsFulfilled,
    contentId,
    isCommentThreadApiAvailable,
    isShared,
    ribbonPanelItemType,
  ]);

  useEffect(() => {
    if (isCommentThreadsListAvailable) {
      const { threads } = commentThreads;
      const commentCountsValue = threads.reduce<CommentCounts>(
        (acc, item) => {
          if (isShared && item.shareWithAll) {
            acc.resolved += item.resolved ? 1 : 0;
            acc.unresolved += !item.resolved ? 1 : 0;
          } else if (!isShared && !item.shareWithAll) {
            acc.resolved += item.resolved ? 1 : 0;
            acc.unresolved += !item.resolved ? 1 : 0;
          }
          acc.contentId = item.contentId;
          return acc;
        },
        {
          contentId: 0,
          resolved: 0,
          unresolved: 0,
        }
      );

      setListCommentCounts(commentCountsValue);
    }
  }, [commentThreads, isCommentThreadsListAvailable, isShared]);

  const commentThreatsSubscription = useCallback(
    ({ data }: CommentThreatsSubscription) => {
      const { commentThreads: threads } = data;
      const { commentThreadsState, viewingStatus: listViewingStatus } =
        data.metadata || {};

      setCommentThreads({
        threads,
        threadsState: commentThreadsState,
        viewingStatus: listViewingStatus,
      });
    },
    []
  );

  useEventSubscription(
    '@comment-set-comment-threads' as CommentEventType,
    commentThreatsSubscription
  );

  const currentViewingStatus = useMemo(() => {
    if (wasPanelOpened && viewingStatusFromCommentsMFE) {
      return viewingStatusFromCommentsMFE as string;
    }

    return viewingStatus === 'resolved'
      ? CommentViewStatus.Resolved
      : CommentViewStatus.Unresolved;
  }, [viewingStatus, viewingStatusFromCommentsMFE, wasPanelOpened]);

  const visibleCommentCounts = useMemo(() => {
    const commentCountsSource =
      isCommentThreadsListAvailable &&
      isPanelOpened &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      ribbonPanelItemType.type === 'comment'
        ? listCommentCounts
        : apiCommentCounts;

    if (currentViewingStatus === CommentViewStatus.Resolved) {
      return commentCountsSource.unresolved + commentCountsSource.resolved;
    }

    return commentCountsSource.unresolved;
  }, [
    apiCommentCounts,
    currentViewingStatus,
    isCommentThreadsListAvailable,
    isPanelOpened,
    listCommentCounts,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    ribbonPanelItemType.type,
  ]);

  return { commentCounts: apiCommentCounts, visibleCommentCounts };
};
